import {
  Card,
  CloseButton,
  EmailComposer,
  ListContext,
  ListContextValue,
  Page,
  PageRoute,
  Stack,
  Typography,
  buildPath,
  buildTemplatePath,
  newToast,
  pn,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { SubmitButton } from "components";
import { BulkSecondLevelValidation, BulkSelectedItemErrors, EmailModel, StudentBulkEmail } from "generated/openApi";
import produce from "immer";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
const routeName = "studentBulkEmail";

export const studentBulkEmailPageRoute: PageRoute = {
  handle: "Batch Email Students",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type StudentBulkEmailPageState = {
  selectedIds: number[];
};

export const StudentBulkEmailPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);
  const { state } = useLocation();
  const [stateParam] = useState<StudentBulkEmailPageState>(state);
  const [bulkEmail, setBulkEmail] = useState<StudentBulkEmail>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [bulkEmail] });
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [selectedItemsErrors, setSelectedItemsErrors] = useState<BulkSelectedItemErrors[]>([]);

  useEffect(() => {
    getBulkEmail(stateParam.selectedIds);
  }, []);

  const getBulkEmail = (ids: number[]) => {
    ApiService.students.getBulkEmail(ids).then((response) => {
      setBulkEmail(response);
      resetUnsavedChanges([response]);
    });
  };

  const send = () => {
    ApiService.common.sendEmails(bulkEmail.emailModel!).then(() => {
      dispatch(setAppToast(newToast("success")));
      listContext.clearSelectedIds?.();
      navigate(`../`);
    });
  };

  const canSend =
    bulkEmail.emailModel?.recipients?.length && bulkEmail.emailModel?.subject && bulkEmail.emailModel?.body;

  return (
    <Page
      isForm
      header={
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="inherit">{studentBulkEmailPageRoute.handle}</Typography>

          <Stack direction="row" alignItems="center" spacing="sm">
            <CloseButton confirm={unsavedChanges} />

            <SubmitButton onClick={() => send()} disabled={!canSend} label="Send" />
          </Stack>
        </Stack>
      }
    >
      <Card>
        <Stack spacing="lg">
          <EmailComposer
            showRecipients
            recipients={bulkEmail.emailModel?.recipients}
            setRecipients={(value) =>
              setBulkEmail(
                produce(bulkEmail, (draft) => {
                  draft.emailModel!.recipients = value;
                })
              )
            }
            showSubject
            subject={bulkEmail.emailModel?.subject}
            setSubject={(value) =>
              setBulkEmail(
                produce(bulkEmail, (draft) => {
                  draft.emailModel!.subject = value!;
                })
              )
            }
            showBody
            bodyHtml={bulkEmail.emailModel?.body}
            setBodyHtml={(value) =>
              setBulkEmail(
                produce(bulkEmail, (draft) => {
                  draft.emailModel!.body = value!;
                })
              )
            }
            bodyHeight="300px"
          />
        </Stack>
      </Card>
    </Page>
  );
};
